import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, ChevronDown, MoreVertical, Shield,
  UserCheck, UserX, Mail, Phone, MapPin, Calendar,
  Download, Trash2, Edit, Eye, X, CheckCircle, AlertTriangle,
  Users, ArrowUpDown, Ban, Lock, Unlock, AlertCircle,
  UserIcon
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import { useSelector } from "react-redux";
import { getAllUsers, useAppDispatch, suspendUser, activateUser, deleteUser, getFavoriteByUserId } from "../../redux";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import { RoleEnum, StatusEnum } from "../../modules/shared/model";
import Spinner from "../../modules/shared/components/Spinner";
import { EditUserDetailModal } from "./components";
import ProfessionalDetailModal from "./components/ProfessionalDetailModal";
import FavoriteButton from "../../components/ui/FavoriteButton";

// RealTimeFavoriteButton component for admin user management
interface RealTimeFavoriteButtonProps {
  user: any;
  currentUserFavorite: any;
}

const RealTimeFavoriteButton = React.memo(({ user, currentUserFavorite }: RealTimeFavoriteButtonProps) => {
  // Initialize with API response data first
  const [isFavorite, setIsFavorite] = useState(user.favorite?.id ? true : false);

  // Listen for changes in currentUserFavorite to update the button state
  useEffect(() => {
    if (currentUserFavorite && currentUserFavorite.userIds) {
      const isFavorited = currentUserFavorite.userIds.some(favUser => favUser.id === user.id.toString());
      setIsFavorite(isFavorited);
    } else {
      // If no favorites exist in Redux state, fall back to API response data
      setIsFavorite(user.favorite?.id ? true : false);
    }
  }, [currentUserFavorite, user.id, user.favorite?.id]);

  return (
    <FavoriteButton
      targetUserId={user.id.toString()}
      size="sm"
      className="p-1 rounded-full bg-white shadow-sm hover:bg-gray-50"
      favoriteData={{
        id: user.favorite?.id,
        isFavorite: isFavorite
      }}
    />
  );
});

RealTimeFavoriteButton.displayName = "RealTimeFavoriteButton";

const AllUsersPage = () => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userType, setUserType] = useState("all");
  const [status, setStatus] = useState(0);
  const [showProfessionalDetails, setShowProfessionalDetails] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [isSearching, setIsSearching] = useState(false);

  const dispatch = useAppDispatch();
  const isLoading = useSelector(selectLoaderValue);
  const { data: allUsers, totalCount, hasNextPage } = useSelector((state: any) => state.admin.allUsers);
  const { currentUserFavorite } = useSelector((state: any) => state.favorites);
  const { user } = useSelector((state: any) => state.auth);

  const getRoleLabel = (id: number) => {
    return RoleEnum[id];
  };

  const getStatusLabel = (id: number) => {
    return StatusEnum[id];
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setShowEditDialog(false);
    setEditingUser(null);
  };

  const handleUserUpdated = () => {
    // Refresh the users list after update
    getAllUser(currentPage, searchQuery, { userType, status, sortBy, sortOrder });
    handleEditDialogClose();
  };

  const handleSuspendUser = async (userId: string) => {
    try {
      const result = await dispatch(suspendUser({ userId }));
      // Refresh the users list after status update
      getAllUser(currentPage, searchQuery, { userType, status, sortBy, sortOrder });

      // Update modal data if it's currently open for this user
      if (showProfessionalDetails && showProfessionalDetails.id === userId) {
        const updatedUser = {
          ...showProfessionalDetails,
          status: "suspended"
        };
        setShowProfessionalDetails(updatedUser);
      }
            } catch (error) {
        }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      const result = await dispatch(activateUser({ userId }));
      // Refresh the users list after status update
      getAllUser(currentPage, searchQuery, { userType, status, sortBy, sortOrder });

      // Update modal data if it's currently open for this user
      if (showProfessionalDetails && showProfessionalDetails.id === userId) {
        const updatedUser = {
          ...showProfessionalDetails,
          status: "active"
        };
        setShowProfessionalDetails(updatedUser);
      }
            } catch (error) {
        }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const result = await dispatch(deleteUser({ userId }));
      // Refresh the users list after deletion
      getAllUser(currentPage, searchQuery, { userType, status, sortBy, sortOrder });

      // Update modal data if it's currently open for this user
      if (showProfessionalDetails && showProfessionalDetails.id === userId) {
        setShowProfessionalDetails(null); // Close modal
      }
            } catch (error) {
        }
  };

  const getAllUser = async (page = 1, search = "", filters: any = {}) => {
    try {
      const params: any = {
        page: page,
        limit: 10,
        search: search,
        status: filters.status,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      // Handle user type filter
      if (filters.userType && filters.userType !== "all") {
        params.roles = [{ id: parseInt(filters.userType) }];
      }

      // Log the parameters being sent (for debugging)
      

      const result = await dispatch(getAllUsers(params));

      // Reset search loading state when API call completes
      if (isSearching) {
        setIsSearching(false);
      }

      return result;
    } catch (err) {
      setIsSearching(false);
    }
  };

  // Load user's favorites on mount
  useEffect(() => {
    if (user?.id && !currentUserFavorite) {
      dispatch(getFavoriteByUserId(user.id));
    }
  }, [user?.id, currentUserFavorite, dispatch]);

  // Initial load and page change
  useEffect(() => {
    getAllUser(currentPage, searchQuery, { userType, status, sortBy, sortOrder });
  }, [currentPage]);

  // Debounced search effect - only trigger when user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== "") {
        setIsSearching(true);
        setCurrentPage(1); // Reset to first page when searching
        getAllUser(1, searchQuery, { userType, status, sortBy, sortOrder });
      } else if (searchQuery === "" && currentPage === 1) {
        // If search is cleared and we're on first page, refresh
        getAllUser(1, "", { userType, status, sortBy, sortOrder });
      }
    }, 800); // Increased debounce time for better UX

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Filter and sort changes - immediate effect
  useEffect(() => {
    if (currentPage === 1) {
      // If already on first page, refresh immediately
      getAllUser(1, searchQuery, { userType, status, sortBy, sortOrder });
    } else {
      // If on other pages, reset to first page and refresh
      setCurrentPage(1);
    }
  }, [userType, status, sortBy, sortOrder]);

  const totalPages = Math.ceil(totalCount / limit);
  

  // Update modal data when allUsers changes
  useEffect(() => {
    if (showProfessionalDetails && showProfessionalDetails.id) {
      const updatedUser = allUsers.find(user => user.id === showProfessionalDetails.id);
      if (updatedUser) {
        const statusId = parseInt(updatedUser.status?.id);
        const actualStatus = StatusEnum[statusId] || "unknown";
        const roleId = parseInt(updatedUser.role?.id);
        const roleLabel = getRoleLabel(roleId);

        // Only update if the status has actually changed
        if (showProfessionalDetails.status !== actualStatus) {
          const updatedUserDetails = {
            ...showProfessionalDetails,
            status: actualStatus,
            type: roleLabel,
            verified: statusId === StatusEnum.active,
          };
          setShowProfessionalDetails(updatedUserDetails);
        }
      }
    }
  }, [allUsers]);


  return (
    <>
      {isLoading && (
        <div className="fixed left-[50%] top-[40%] z-[1000px]">
          <Spinner color="green" />
        </div>
      )}
      <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
        <AdminHeader />
        <main className="flex-1 lg:pl-64 pt-16">
          <div className={`p-6 ${isLoading ? "opacity-25" : "opacity-100"}`}>
            <div className="max-w-7xl mx-auto space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                  <p className="text-gray-600 mt-1">Manage and monitor all users across the platform</p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Export Users
                  </Button>
                </div>
              </div>

              <Card className="overflow-hidden">
                <div className="p-4 border-b">
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="relative flex-1 sm:min-w-[300px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by name, email, or location..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-10 py-2 border rounded-lg"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => {
                              setSearchQuery("");
                              setCurrentPage(1);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                        {/* Search loading indicator */}
                        {isSearching && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          </div>
                        )}
                      </div>
                      {/* <Button
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center gap-2"
                      >
                        <Filter className="h-5 w-5" />
                        Filters
                      </Button> */}
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Sort:</span>
                        <select
                          value={`${sortBy}-${sortOrder}`}
                          onChange={(e) => {
                            const [field, order] = e.target.value.split("-");
                            setSortBy(field);
                            setSortOrder(order);
                          }}
                          className="px-4 py-2 border rounded-lg text-sm"
                        >
                          <option value="createdAt-desc">Newest First</option>
                          <option value="createdAt-asc">Oldest First</option>
                          <option value="firstName-asc">Name A-Z</option>
                          <option value="firstName-desc">Name Z-A</option>
                          <option value="email-asc">Email A-Z</option>
                          <option value="email-desc">Email Z-A</option>
                          <option value="lastName-asc">Last Name A-Z</option>
                          <option value="lastName-desc">Last Name Z-A</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {showFilters && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-4 pt-4 border-t"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium text-gray-700">Filters</h3>
                          <div className="flex items-center gap-2">
                            {/* Active filters indicator */}
                            {(userType !== "all" || status !== 0 || searchQuery || sortBy !== "createdAt" || sortOrder !== "desc") && (
                              <div className="text-xs text-gray-500">
                                Active filters: {[
                                  userType !== "all" && `Type: ${userType}`,
                                  status !== 0 && `Status: ${status}`,
                                  searchQuery && `Search: "${searchQuery}"`,
                                  (sortBy !== "createdAt" || sortOrder !== "desc") && `Sort: ${sortBy} ${sortOrder === "asc" ? "↑" : "↓"}`
                                ].filter(Boolean).join(", ")}
                              </div>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setUserType("all");
                                setStatus(0);
                                setSortBy("createdAt");
                                setSortOrder("desc");
                                setSearchQuery("");
                                setCurrentPage(1);
                              }}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Clear All Filters
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              User Type
                            </label>
                            <select
                              value={userType}
                              onChange={(e) => {
                                setUserType(e.target.value);
                              }}
                              className="w-full px-4 py-2 border rounded-lg"
                            >
                              <option value="all">All Types</option>
                              <option value="2">Clients</option>
                              <option value="3">International Clients</option>
                              <option value="4">Professionals</option>
                              <option value="5">International Professionals</option>
                              <option value="6">Emergency Professionals</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Status
                            </label>
                            <select
                              value={status}
                              onChange={(e) => {
                                setStatus(Number(e.target.value));
                              }}
                              className="w-full px-4 py-2 border rounded-lg"
                            >
                              <option value={0}>All Status</option>
                              <option value={1}>Active</option>
                              <option value={2}>Inactive</option>
                              <option value={3}>Suspended</option>
                              <option value={4}>Deleted</option>
                              <option value={5}>Pending</option>
                              <option value={6}>Approved</option>
                              <option value={7}>Rejected</option>
                              <option value={8}>Completed</option>
                              <option value={9}>Cancelled</option>
                              <option value={10}>Expired</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Sort By
                            </label>
                            <select
                              value={`${sortBy}-${sortOrder}`}
                              onChange={(e) => {
                                const [field, order] = e.target.value.split("-");
                                setSortBy(field);
                                setSortOrder(order);
                              }}
                              className="w-full px-4 py-2 border rounded-lg"
                            >
                              <option value="createdAt-desc">Newest First</option>
                              <option value="createdAt-asc">Oldest First</option>
                              <option value="firstName-asc">Name A-Z</option>
                              <option value="firstName-desc">Name Z-A</option>
                              <option value="email-asc">Email A-Z</option>
                              <option value="email-desc">Email Z-A</option>
                              <option value="lastName-asc">Last Name A-Z</option>
                              <option value="lastName-desc">Last Name Z-A</option>
                            </select>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="overflow-x-auto">
                  {/* Loading overlay for filter changes */}
                  {isLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-gray-600">Loading users...</span>
                      </div>
                    </div>
                  )}
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers(allUsers.map(user => user.id));
                              } else {
                                setSelectedUsers([]);
                              }
                            }}
                          />
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Joined
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Profile URL
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Favorite
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {allUsers.length > 0 ? allUsers.map((user) => {
                        const roleId = parseInt(user.role?.id);
                        const statusId = parseInt(user.status?.id);
                        const roleLabel = getRoleLabel(roleId);
                        const statusLabel = getStatusLabel(statusId);
                        const fullName = `${user.firstName} ${user.lastName}`;
                        const registeredDate = new Date(user.createdAt).toLocaleDateString();

                        // Get the actual status string from the enum
                        const actualStatus = StatusEnum[statusId] || "unknown";

                        return (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                checked={selectedUsers.includes(user.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsers([...selectedUsers, user.id]);
                                  } else {
                                    setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                  }
                                }}
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                  <UserIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <div className="ml-4">
                                  <div className="font-medium text-gray-900">{fullName}</div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge variant={undefined} className={
                                roleLabel?.includes("professional")
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-blue-100 text-blue-700"
                              }>
                                {roleLabel
                                  ?.replaceAll("_", " ")
                                  .replace(/\b\w/g, l => l.toUpperCase())}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className={`h-2.5 w-2.5 rounded-full mr-2 ${actualStatus === "active" ? "bg-green-500" :
                                    actualStatus === "suspended" ? "bg-red-500" :
                                      actualStatus === "pending" ? "bg-yellow-500" :
                                        actualStatus === "inactive" ? "bg-gray-500" :
                                          actualStatus === "approved" ? "bg-blue-500" :
                                            actualStatus === "rejected" ? "bg-red-600" :
                                              actualStatus === "completed" ? "bg-green-600" :
                                                actualStatus === "cancelled" ? "bg-gray-600" :
                                                  actualStatus === "expired" ? "bg-orange-500" :
                                                    actualStatus === "deleted" ? "bg-red-700" :
                                                      "bg-blue-500"
                                  }`} />
                                <span className="capitalize">{actualStatus}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                              <div className="flex items-center text-gray-500">
                                <MapPin className="h-4 w-4 mr-2" />
                                {user.location || "N/A"}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                              {registeredDate}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                              {roleLabel?.includes("client") ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const profileUrl = `${window.location.origin}/client/profile/${user.id}`;
                                    navigator.clipboard.writeText(profileUrl)
                                      .then(() => {
                                        // You can add a toast notification here if you have one
                                        alert("Profile URL copied to clipboard!");
                                      })
                                      .catch(() => {
                                        // Fallback for older browsers
                                        const textArea = document.createElement("textarea");
                                        textArea.value = profileUrl;
                                        document.body.appendChild(textArea);
                                        textArea.select();
                                        document.execCommand("copy");
                                        document.body.removeChild(textArea);
                                        alert("Profile URL copied to clipboard!");
                                      });
                                  }}
                                  className="text-blue-600 hover:text-blue-700 text-sm"
                                >
                                  Share
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const profileUrl = `${window.location.origin}/myprofile/${user.id}`;
                                    navigator.clipboard.writeText(profileUrl)
                                      .then(() => {
                                        // You can add a toast notification here if you have one
                                        alert("Profile URL copied to clipboard!");
                                      })
                                      .catch(() => {
                                        // Fallback for older browsers
                                        const textArea = document.createElement("textarea");
                                        textArea.value = profileUrl;
                                        document.body.appendChild(textArea);
                                        textArea.select();
                                        document.execCommand("copy");
                                        document.body.removeChild(textArea);
                                        alert("Profile URL copied to clipboard!");
                                      });
                                  }}
                                  className="text-blue-600 hover:text-blue-700 text-sm"
                                >
                                  Share
                                </Button>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <RealTimeFavoriteButton user={user} currentUserFavorite={currentUserFavorite} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const enrichedUser = {
                                      ...user,
                                      name: `${user.firstName} ${user.lastName}`,
                                      status: actualStatus,
                                      type: roleLabel,
                                      location: user.location || "N/A",
                                      registeredDate: new Date(user.createdAt).toLocaleDateString(),
                                      orders: 12,
                                      totalSpent: "$500",
                                      lastLogin: "2025-06-01",
                                      profileCompletion: 80,
                                      verified: parseInt(user.status?.id) === StatusEnum.active,
                                    };

                                    // Check user type
                                    const isProfessional = roleLabel?.toLowerCase().includes("professional");
                                    const isClient = roleLabel?.toLowerCase().includes("client");
                                    const isInternationalProfessional = roleLabel?.toLowerCase().includes("international professional");
                                    const isInternationalClient = roleLabel?.toLowerCase().includes("international client");

                                    // Use ProfessionalDetailModal for both clients and professionals
                                    let userData = { ...enrichedUser };

                                    if (isProfessional) {
                                      // Professional-specific data
                                      userData = {
                                        ...enrichedUser,
                                        profession: "Software Developer", // This should come from user data
                                        serviceArea: user.location || "N/A",
                                        timeZone: "UTC+0", // This should come from user data
                                        completedJobs: 25,
                                        totalEarnings: "$2,500",
                                        expertise: ["React", "Node.js", "TypeScript"], // This should come from user data
                                        languages: isInternationalProfessional ? ["English", "Spanish"] : [],
                                        serviceRegions: isInternationalProfessional ? ["North America", "Europe"] : [],
                                        internationalVerification: isInternationalProfessional ? {
                                          businessLicense: true,
                                          taxCompliance: false,
                                          backgroundCheck: true,
                                          insuranceCoverage: false
                                        } : null,
                                        userType: "professional"
                                      };
                                    } else if (isClient) {
                                      // Client-specific data
                                      userData = {
                                        ...enrichedUser,
                                        profession: "Client", // Generic profession for clients
                                        serviceArea: user.location || "N/A",
                                        timeZone: "UTC+0", // This should come from user data
                                        completedJobs: 0, // Clients don't have completed jobs
                                        totalEarnings: "$0", // Clients don't earn, they spend
                                        totalSpent: "$500", // This is more relevant for clients
                                        expertise: [], // Clients don't have expertise
                                        languages: isInternationalClient ? ["English", "Spanish"] : [],
                                        serviceRegions: isInternationalClient ? ["North America", "Europe"] : [],
                                        internationalVerification: isInternationalClient ? {
                                          businessLicense: false,
                                          taxCompliance: false,
                                          backgroundCheck: true,
                                          insuranceCoverage: false
                                        } : null,
                                        userType: "client"
                                      };
                                    } else {
                                      // Default user data
                                      userData = {
                                        ...enrichedUser,
                                        profession: "User",
                                        serviceArea: user.location || "N/A",
                                        timeZone: "UTC+0",
                                        completedJobs: 0,
                                        totalEarnings: "$0",
                                        expertise: [],
                                        languages: [],
                                        serviceRegions: [],
                                        internationalVerification: null,
                                        userType: "user"
                                      };
                                    }

                                    setShowProfessionalDetails(userData);
                                  }}
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  View Details
                                </Button>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={9} className="px-6 py-12 text-center">
                            <div className="text-gray-500">
                              <UserIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                              <p className="text-lg font-medium mb-2">No users found</p>
                              <p className="text-sm">
                                {searchQuery || userType !== "all" || status !== 0
                                  ? "Try adjusting your search or filters"
                                  : "No users available at the moment"
                                }
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="px-6 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-700">
                      Showing {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, totalCount)} of {totalCount} users
                    </span>
                    <span className="text-gray-500">
                      (Page {currentPage} of {totalPages})
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={currentPage === totalPages || !hasNextPage || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </main>
        <br></br>
        <br></br>
        <br></br>
        <br></br>

        {/* User Details Modal (for both clients and professionals) */}
        <AnimatePresence>
          {showProfessionalDetails && (
            <ProfessionalDetailModal
              professional={showProfessionalDetails}
              onClose={() => setShowProfessionalDetails(null)}
              onEditUser={handleEditUser}
              onSuspendUser={handleSuspendUser}
              onActivateUser={handleActivateUser}
              onDeleteUser={handleDeleteUser}
              isInternational={showProfessionalDetails?.type?.toLowerCase().includes("international")}
            />
          )}
        </AnimatePresence>

        {/* Edit User Dialog */}
        <EditUserDetailModal
          isOpen={showEditDialog}
          onClose={handleEditDialogClose}
          user={editingUser}
          onUserUpdated={handleUserUpdated}
        />
      </div>
    </>
  );
};

export default AllUsersPage;